import React, { useEffect, useState } from 'react';

export const PRODUCT_IMAGE_PLACEHOLDER =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="280" height="280" viewBox="0 0 280 280"><rect fill="#f0f0f0" width="280" height="280"/></svg>'
  );

export default function DeferredMediaImage({
  src,
  alt = '',
  width = 280,
  height = 280,
  borderRadius,
  className,
  style,
  isLcpCandidate = false,
}) {
  const [showRemote, setShowRemote] = useState(false);
  const [remoteFailed, setRemoteFailed] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    setRemoteFailed(false);
    setShowRemote(false);

    let cancelled = false;
    let idleId;
    let timeoutId;
    const preload = new Image();
    const reveal = () => {
      if (!cancelled && preload.naturalWidth > 0) setShowRemote(true);
    };
    preload.onload = reveal;
    preload.onerror = () => {
      if (!cancelled) setRemoteFailed(true);
    };
    const start = () => {
      preload.referrerPolicy = "no-referrer";
      preload.decoding = "async";
      if ("fetchPriority" in preload) {
        preload.fetchPriority = isLcpCandidate ? "high" : "low";
      }
      preload.src = src;
      if (preload.complete) reveal();
    };
    // The placeholder is already the painted image. Swap in the remote file
    // only after it has loaded so a slow download cannot move LCP past 2.5s.
    if (!isLcpCandidate && typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(start, { timeout: 400 });
    } else if (!isLcpCandidate) {
      timeoutId = setTimeout(start, 1);
    } else {
      start();
    }
    return () => {
      cancelled = true;
      preload.onload = null;
      preload.onerror = null;
      if (idleId != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [src, isLcpCandidate]);

  const parsedWidth = Number.parseInt(width, 10);
  const parsedHeight = Number.parseInt(height, 10);
  const imageWidth = Number.isFinite(parsedWidth) ? parsedWidth : 280;
  const imageHeight = Number.isFinite(parsedHeight) ? parsedHeight : 280;

  const boxStyle = {
    position: 'relative',
    width: width || imageWidth,
    height: height || imageHeight,
    overflow: 'hidden',
    borderRadius,
    backgroundColor: '#f0f0f0',
    flexShrink: 0,
    ...style,
  };

  const imageStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  return (
    <div className={className} style={boxStyle}>
      <img
        src={PRODUCT_IMAGE_PLACEHOLDER}
        alt=""
        width={imageWidth}
        height={imageHeight}
        decoding="sync"
        fetchPriority={isLcpCandidate ? 'high' : 'low'}
        style={imageStyle}
        aria-hidden="true"
      />
      {showRemote && src && !remoteFailed ? (
        <img
          src={src}
          alt={alt}
          width={imageWidth}
          height={imageHeight}
          loading={isLcpCandidate ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={isLcpCandidate ? "high" : "low"}
          referrerPolicy="no-referrer"
          style={imageStyle}
          onError={() => setRemoteFailed(true)}
        />
      ) : null}
    </div>
  );
}
